/**
 * webappPreviewIcons — pixel-identical ports of the WebApp's
 * SessionPlayIcon and QuickTapVideoIcon (TheTappingSolutionWeb
 * src/components/icons/{SessionPlayIcon,QuickTapVideoIcon}.tsx).
 *
 * Used in the CMS preview cards (PreviewCard, PreviewCardQT) so the
 * library-tile preview shows the *actual* glyph end users see —
 * including the locked-padlock overlay when the content is premium.
 *
 * Why duplicated rather than imported:
 *   • The WebApp and CMS are separate React apps — no shared module
 *     graph. The visual is small and stable; the SVG paths are
 *     literal hex coordinates so a copy keeps the preview honest
 *     even if the WebApp refactors its icon library.
 *   • TypeScript → JSX, drop React.memo (not worth the bundle), and
 *     swap Tailwind className wiring for inline style since the
 *     CMS doesn't share the WebApp's Tailwind config.
 *
 * Locked semantics:
 *   • `isLocked` follows the WebApp convention: true → grey icon +
 *     padlock overlay. Pass `isLocked={!isFree}` for sessions and
 *     `isLocked={isPremium}` for Quick Taps to preview the
 *     free-user view of premium content (the most common viewer).
 */
import React from 'react';

/* ─── Shared lock-badge overlay ────────────────────────────────── */

function LockBadge() {
  // Identical geometry to SessionPlayIcon.tsx lock paths.
  return (
    <svg
      viewBox="21.44 4 10.56 10.713"
      fill="none"
      aria-hidden="true"
      focusable="false"
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        height: '42%',
        width: '42%',
        transform: 'translateY(-26%)',
      }}
    >
      <path
        d="M26.7199 14.713C29.636 14.713 31.9999 12.3148 31.9999 9.35652C31.9999 6.3982 29.636 4 26.7199 4C23.8039 4 21.4399 6.3982 21.4399 9.35652C21.4399 12.3148 23.8039 14.713 26.7199 14.713Z"
        fill="#9B9B9B"
      />
      <path
        d="M28.9599 8.70723C28.9599 8.70723 28.7999 8.54492 28.6399 8.54492H28.4799V7.73332C28.4799 7.24637 28.3199 6.75941 27.9999 6.43477C27.6799 6.11013 27.1999 5.94781 26.7199 5.94781C26.2399 5.94781 25.7599 6.11013 25.4399 6.43477C25.1199 6.75941 24.9599 7.24637 24.9599 7.73332V8.54492H24.7999C24.6399 8.54492 24.6399 8.54492 24.4799 8.70723C24.4799 8.70723 24.3199 8.86955 24.3199 9.03187V11.4667C24.3199 11.629 24.3199 11.629 24.4799 11.7913C24.4799 11.7913 24.6399 11.9536 24.7999 11.9536H28.7999C28.9599 11.9536 28.9599 11.9536 29.1199 11.7913C29.1199 11.7913 29.2799 11.629 29.2799 11.4667V9.03187C28.9599 8.86955 28.9599 8.86955 28.9599 8.70723ZM27.6799 8.54492H25.5999V7.73332C25.5999 7.40868 25.7599 7.24637 25.9199 6.92173C26.0799 6.75941 26.3999 6.59709 26.7199 6.59709C27.0399 6.59709 27.1999 6.75941 27.5199 6.92173C27.6799 7.08405 27.8399 7.40868 27.8399 7.73332V8.54492H27.6799Z"
        fill="white"
      />
    </svg>
  );
}

/* ─── Session play icon (white circle + teal play triangle) ────── */

export function WebAppSessionPlayIcon({
  size = 36,
  isLocked = false,
  gradientStart = '#65C8DA',
  gradientEnd = '#048CB9',
  gradientId = 'cmsPreviewSessionGrad',
}) {
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        overflow: 'visible',
      }}
    >
      <svg
        viewBox="0 0 32 32"
        fill="none"
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          overflow: 'visible',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
        }}
        aria-hidden="true"
      >
        {/* WebApp uses `scale(1.33333) translate(-8, -4)` to position
            the 32-unit base circle. Same here. */}
        <g transform="scale(1.33333) translate(-8, -4)">
          <path
            d="M20 28C26.6274 28 32 22.6274 32 16C32 9.37258 26.6274 4 20 4C13.3726 4 8 9.37258 8 16C8 22.6274 13.3726 28 20 28Z"
            fill="white"
          />
          <path
            d="M17.6 9.92C16.32 9.28 15.52 9.76 15.52 11.04V20.8C15.52 22.08 16.48 22.72 17.6 22.08L26.24 17.12C27.36 16.48 27.36 15.36 26.24 14.72L17.6 9.92Z"
            fill={isLocked ? '#9B9B9B' : `url(#${gradientId})`}
          />
        </g>
        {!isLocked && (
          <defs>
            <linearGradient
              id={gradientId}
              x1="21.228"
              y1="9.59792"
              x2="21.228"
              y2="22.4021"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor={gradientStart} />
              <stop offset="1" stopColor={gradientEnd} />
            </linearGradient>
          </defs>
        )}
      </svg>
      {isLocked && <LockBadge />}
    </div>
  );
}

/* ─── Quick Tap video icon (white circle + camera glyph) ───────── */

export function WebAppQuickTapVideoIcon({
  size = 36,
  isLocked = false,
  gradientId = 'cmsPreviewQuickTapGrad',
}) {
  // WebApp expands the SVG's height when locked so the lock badge has
  // room to sit above the circle. We mimic that by inflating the
  // wrapper height; the SVG viewBox stays the same but the wrapping
  // height grows so absolute positioning has somewhere to land.
  const wrapperHeight = isLocked ? size * 1.33333 : size;

  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: wrapperHeight,
        overflow: 'visible',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        fill="none"
        width={size}
        height={wrapperHeight}
        style={{
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
        }}
        aria-hidden="true"
      >
        <g transform="scale(1.33333) translate(-8, -5)">
          <path
            d="M20 28C26.6274 28 32 22.6274 32 16C32 9.37258 26.6274 4 20 4C13.3726 4 8 9.37258 8 16C8 22.6274 13.3726 28 20 28Z"
            fill="white"
            transform={isLocked ? 'translate(0, 4.35)' : 'translate(0, 1)'}
          />

          {/* Camera glyph */}
          <g transform={isLocked ? 'translate(13.5, 15.35)' : 'translate(13.5, 12)'}>
            <path
              d="M12.7396 0.460062C12.6786 0.436666 12.62 0.424919 12.5636 0.424919C12.4369 0.424919 12.3315 0.469544 12.247 0.558547L9.41116 3.38518V2.22503C9.41116 1.66715 9.21295 1.19026 8.81653 0.794132C8.42009 0.39805 7.94289 0.199997 7.38452 0.199997H2.43072C1.87252 0.199997 1.39517 0.39805 0.998701 0.794132C0.602383 1.19026 0.404175 1.66715 0.404175 2.22503V7.17504C0.404175 7.73287 0.602383 8.20973 0.998725 8.60584C1.39519 9.00195 1.87255 9.2 2.43074 9.2H7.38457C7.94291 9.2 8.42014 9.00195 8.81658 8.60584C9.213 8.20973 9.41121 7.7329 9.41121 7.17504V6.00777L12.247 8.84142C12.3316 8.9304 12.437 8.975 12.5637 8.975C12.62 8.975 12.6787 8.96328 12.7396 8.93993C12.9227 8.86021 13.0141 8.72198 13.0141 8.52511V0.875009C13.014 0.678039 12.9226 0.539781 12.7396 0.460062Z"
              fill={isLocked ? '#9B9B9B' : `url(#${gradientId})`}
            />
          </g>

          {/* Inline lock circle + body — when locked, the QuickTap
              variant tucks the lock INSIDE the same SVG (the
              WebApp's locked layout) rather than overlaying it
              externally like SessionPlayIcon. */}
          {isLocked && (
            <>
              <path
                d="M26.7199 14.713C29.636 14.713 31.9999 12.3148 31.9999 9.35652C31.9999 6.3982 29.636 4 26.7199 4C23.8039 4 21.4399 6.3982 21.4399 9.35652C21.4399 12.3148 23.8039 14.713 26.7199 14.713Z"
                fill="#9B9B9B"
              />
              <path
                d="M28.9599 8.70723C28.9599 8.70723 28.7999 8.54492 28.6399 8.54492H28.4799V7.73332C28.4799 7.24637 28.3199 6.75941 27.9999 6.43477C27.6799 6.11013 27.1999 5.94781 26.7199 5.94781C26.2399 5.94781 25.7599 6.11013 25.4399 6.43477C25.1199 6.75941 24.9599 7.24637 24.9599 7.73332V8.54492H24.7999C24.6399 8.54492 24.6399 8.54492 24.4799 8.70723C24.4799 8.70723 24.3199 8.86955 24.3199 9.03187V11.4667C24.3199 11.629 24.3199 11.629 24.4799 11.7913C24.4799 11.7913 24.6399 11.9536 24.7999 11.9536H28.7999C28.9599 11.9536 28.9599 11.9536 29.1199 11.7913C29.1199 11.7913 29.2799 11.629 29.2799 11.4667V9.03187C28.9599 8.86955 28.9599 8.86955 28.9599 8.70723ZM27.6799 8.54492H25.5999V7.73332C25.5999 7.40868 25.7599 7.24637 25.9199 6.92173C26.0799 6.75941 26.3999 6.59709 26.7199 6.59709C27.0399 6.59709 27.1999 6.75941 27.5199 6.92173C27.6799 7.08405 27.8399 7.40868 27.8399 7.73332V8.54492H27.6799Z"
                fill="white"
              />
            </>
          )}
        </g>
        {!isLocked && (
          <defs>
            <linearGradient
              id={gradientId}
              x1="6.70914"
              y1="0.199997"
              x2="6.70914"
              y2="9.2"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#65C8DA" />
              <stop offset="1" stopColor="#048CB9" />
            </linearGradient>
          </defs>
        )}
      </svg>
    </div>
  );
}
